import { Component, useEffect, useMemo, useState, type ChangeEvent, type ErrorInfo, type ReactNode } from "react";
import { Loading } from "@/components/Loading";
import { maskSecret } from "@/utils/format";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { StatusPill, MERCHANT_STATUS_TONES } from "@/components/StatusPill";
import { Empty } from "@/components/Empty";
import { Notice } from "@/components/Notice";
import { Dash } from "@/components/Dash";
import { useConfirm } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Pencil, Plus, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatDate, formatMonth } from "@/utils/dates";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { MerchantSubresourceSections } from "@/features/merchants/components/MerchantSubresourceSections";
import { ApiError } from "@/lib/apiClient";
import { FIELD_MAX } from "@/lib/fieldLimits";
import { merchants, merchantSubresources, type MerchantDetail, type MerchantStatementSummary, type UserRef, type MerchantStoreRow } from "@/features/merchants/api/merchants.api";
import { tickets as ticketsApi, type TicketListItem } from "@/features/tickets/api/tickets.api";
import { TicketStatusPill, PriorityBadge } from "@/features/tickets/components/TicketBadges";
import { useBackOrigin, useBackTo, type BackOrigin } from "@/hooks/useBackTo";
import { applicationsV2 } from "@/features/applications/api/applications.api";
import { users as usersApi } from "@/features/users/api/users.api";
import { UserSelect } from "@/features/users/components/UserSelect";

const MERCHANT_TABS = [
  { key: "profile", label: "Profile" },
  { key: "batches", label: "Batches" },
  { key: "authorizations", label: "Authorizations" },
  { key: "statements", label: "Statements" },
  { key: "saas-fees", label: "SaaS fees" },
  { key: "tickets", label: "Tickets" },
] as const;

type MerchantTabKey = (typeof MERCHANT_TABS)[number]["key"];

const TAB_ICONS: Record<MerchantTabKey, string> = {
  profile: "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
  batches: "M3 3h7v7H3z M14 3h7v7h-7z M14 14h7v7h-7z M3 14h7v7H3z",
  authorizations: "M9 12l2 2 4-4 M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c2.4 0 4.58.94 6.2 2.47",
  statements: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8",
  "saas-fees": "M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z M2 10h20",
  tickets: "M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z",
};

type LoadState =
  | { kind: "loading" }
  | { kind: "ok"; data: MerchantDetail }
  | { kind: "not-found" }
  | { kind: "forbidden" }
  | { kind: "error"; message: string };

export default function MerchantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const backTo = useBackTo("/merchants");
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    if (!id) {
      setState({ kind: "error", message: "Missing merchant id in URL." });
      return;
    }
    let cancelled = false;
    setState({ kind: "loading" });
    merchants.get(id)
      .then((data) => { if (!cancelled) setState({ kind: "ok", data }); })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 404) {
          setState({ kind: "not-found" });
        } else if (e instanceof ApiError && e.status === 403) {
          setState({ kind: "forbidden" });
        } else {
          setState({ kind: "error", message: e instanceof Error ? e.message : "Failed to load merchant." });
        }
      });
    return () => { cancelled = true; };
  }, [id]);

  return (
    <>
      <div className="mb-4">
        <Link to={backTo} className="text-sm text-muted-foreground hover:underline">
          ← Back
        </Link>
      </div>

      {state.kind === "loading" && (
        <Loading>Loading merchant…</Loading>
      )}

      {state.kind === "not-found" && (
        <Notice tone="info">
          Merchant not found, or you don't have permission to view it. The
          API returns 404 for both cases so we don't leak which merchants
          exist outside your scope.
        </Notice>
      )}

      {state.kind === "forbidden" && (
        <Notice tone="warn">
          You don't have permission to view merchants. Ask an admin to
          assign a role that grants <code>20.05</code> (View merchants).
        </Notice>
      )}

      {state.kind === "error" && (
        <Notice tone="error">{state.message}</Notice>
      )}

      {state.kind === "ok" && (
        <DetailErrorBoundary>
          <MerchantBody m={state.data} />
        </DetailErrorBoundary>
      )}
    </>
  );
}

class DetailErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Merchant detail render error:", error, info.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <Notice tone="error">
          Something went wrong rendering this merchant. {this.state.error.message}
          <span className="block text-xs text-red-600/70 mt-1">
            (Details are in the browser console.)
          </span>
        </Notice>
      );
    }
    return this.props.children;
  }
}

function MerchantBody({ m }: { m: MerchantDetail }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab") ?? "profile";
  const activeTab: MerchantTabKey =
    (MERCHANT_TABS.find((t) => t.key === rawTab)?.key) ?? "profile";

  const ticketOrigin = useBackOrigin();
  const [mid, setMid] = useState(m.mid);
  const [allTickets, setAllTickets] = useState<TicketListItem[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    ticketsApi.listAll({ merchantId: m.id })
      .then((rows) => { if (!cancelled) setAllTickets(rows); })
      .catch(() => { if (!cancelled) setAllTickets([]); });
    return () => { cancelled = true; };
  }, [m.id]);
  const merchantTickets = useMemo(
    () => (allTickets ?? []).filter((t) => t.merchantId === m.id),
    [allTickets, m.id],
  );

  function selectTab(key: MerchantTabKey) {
    const next = new URLSearchParams(searchParams);
    if (key === "profile") next.delete("tab");
    else next.set("tab", key);
    setSearchParams(next, { replace: false });
  }

  return (
    <>
      <header className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 truncate">{m.dbaName}</h1>
          <span className="text-slate-300 text-2xl leading-none">/</span>
          <MidCell mid={mid} />
          <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
            Acquiring
          </span>
          <StatusPill value={m.status} map={MERCHANT_STATUS_TONES} />
          {m.recentStatements[0]?.processor && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
              {m.recentStatements[0].processor}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-xs text-slate-500 hidden sm:block">
            Branch: <span className="font-medium text-slate-700">BLogic Systems</span>
          </div>
          <Button asChild className="whitespace-nowrap">
            <Link to={`/tickets/new?merchantId=${m.id}`} state={ticketOrigin}>+ Ticket</Link>
          </Button>
        </div>
      </header>

      <MerchantTabStrip
        activeTab={activeTab}
        ticketCount={allTickets ? merchantTickets.length : undefined}
        onSelect={selectTab}
      />

      <div className="mt-5">
        {activeTab === "profile" && <ProfileTab m={m} mid={mid} onMidChange={setMid} />}
        {activeTab === "statements" && (
          <Panel title={`Monthly statements (${m.statementCount})`} wide>
            {m.recentStatements.length === 0 ? (
              <Empty>
                No residual data on file yet. Once the residual importer
                (TSYS / Maverick / VizyPay) lands, monthly rollups appear here.
              </Empty>
            ) : (
              <StatementsTable rows={m.recentStatements} totalCount={m.statementCount} />
            )}
          </Panel>
        )}
        {activeTab === "tickets" && (
          <TicketsTab
            merchantId={m.id}
            tickets={allTickets ? merchantTickets : null}
            origin={ticketOrigin}
          />
        )}
        {activeTab === "batches" && (
          <ComingSoonTab
            title="Batches"
            body="Daily batch detail isn't ingested yet — only monthly residual rollups are. This lights up once daily transaction data is connected."
          />
        )}
        {activeTab === "authorizations" && (
          <ComingSoonTab
            title="Authorizations"
            body="Authorization-level transaction data isn't ingested yet. This lights up once daily transaction data is connected."
          />
        )}
        {activeTab === "saas-fees" && (
          <ComingSoonTab
            title="SaaS fees"
            body="Per-merchant SaaS fee billing isn't wired up yet."
          />
        )}
      </div>
    </>
  );
}

function MidCell({ mid }: { mid: string }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="font-mono text-lg text-slate-600 font-medium">{mid}</span>
      {mid.startsWith("PENDING-") && (
        <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 ring-1 ring-amber-200">
          MID pending
        </span>
      )}
    </span>
  );
}

function MidPanel({
  merchant, mid, onMidChange,
}: {
  merchant: MerchantDetail;
  mid: string;
  onMidChange: (mid: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function openDialog() {
    setDraft("");
    setErr(null);
    setOpen(true);
  }

  async function save() {
    const next = draft.trim();
    if (!next) {
      setErr("Enter a Merchant ID.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await merchants.setMid(merchant.id, next);
      onMidChange(next);
      setOpen(false);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Couldn't set MID.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <CollapsiblePanel
        title={`Merchant Number (MID): ${mid}`}
        storageKey="mid"
        defaultOpen
        action={
          <Button type="button" size="sm" variant="outline" onClick={openDialog}>
            <Pencil />
            Edit Merchant ID
          </Button>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
          <KpiField label="Risk level" value="Low Eligible" />
          <KpiField label="Identifier" value={mid} />
          <KpiField label="Discover identifier" value="(not set)" />
          <KpiField label="AMEX OptBlue #" value="(not set)" />
        </div>
        <div className="pt-3 mt-3 border-t border-slate-100">
          <KpiField label="Note" value={merchant.notes || <Dash />} />
        </div>
      </CollapsiblePanel>

      <Dialog open={open} onOpenChange={(next) => { if (!busy) { setOpen(next); if (!next) setErr(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Merchant ID</DialogTitle>
            <DialogDescription>Enter the Merchant ID assigned to this merchant.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="mid-current">Current Merchant ID</Label>
              <Input id="mid-current" value={mid} readOnly disabled className="font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mid-new">
                New Merchant ID <span className="text-destructive">*</span>
              </Label>
              <Input
                id="mid-new"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Enter Merchant ID"
                maxLength={FIELD_MAX.merchant.mid}
                className="font-mono"
                autoFocus
              />
              <p className="text-xs text-muted-foreground">Max {FIELD_MAX.merchant.mid} characters</p>
            </div>
            <Notice tone="warn" className="text-xs">
              <span className="flex gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
                <span>
                  Changing the MID affects reconciliation and fund settlement for all future
                  transactions. Double-check before confirming.
                </span>
              </span>
            </Notice>
            {err && <p className="text-xs text-destructive">{err}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button type="button" onClick={save} disabled={busy}>
              {busy ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const AGENT_NONE = "__none__";
const AGENT_NONE_OPTIONS = { owner: [{ value: AGENT_NONE, label: "— Select —" }], associate: [{ value: AGENT_NONE, label: "— None —" }] };

function AgentRow({
  merchantId, kind, label, initial,
}: {
  merchantId: string;
  kind: "owner" | "associate";
  label: string;
  initial: UserRef | null;
}) {
  const [current, setCurrent] = useState<UserRef | null>(initial);
  const [editing, setEditing] = useState(false);
  const [sel, setSel] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function openEdit() {
    setErr(null);
    setSel(current?.id ?? "");
    setEditing(true);
  }

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      if (kind === "owner") {
        if (!sel) { setErr("Pick an agent."); setBusy(false); return; }
        await merchants.setOwner(merchantId, sel);
      } else {
        await merchants.setAssociate(merchantId, sel || null);
      }
      const u = sel ? await usersApi.get(sel).catch(() => null) : null;
      setCurrent(u ? { id: u.id, name: u.name, email: u.email, userNumber: u.userNumber } : null);
      setEditing(false);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Couldn't save the change.");
    } finally {
      setBusy(false);
    }
  }

  const display = current
    ? `${current.userNumber != null ? `#${current.userNumber} ` : ""}${current.name}`
    : null;

  return (
    <div className="pt-3 mt-3 border-t border-slate-100">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
        {!editing && (
          <Button type="button" size="sm" variant="link" className="h-auto p-0" onClick={openEdit}>
            {kind === "owner" ? "Reassign →" : "Change →"}
          </Button>
        )}
      </div>
      {editing ? (
        <div className="mt-1.5 space-y-2">
          <UserSelect
            value={sel || AGENT_NONE}
            onValueChange={(v) => setSel(v === AGENT_NONE ? "" : v)}
            pinnedOptions={AGENT_NONE_OPTIONS[kind]}
            onError={() => setErr("You don't have permission to list agents.")}
          />
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" onClick={save} disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => { setEditing(false); setErr(null); }}>Cancel</Button>
            {err && <span className="text-xs text-destructive">{err}</span>}
          </div>
        </div>
      ) : (
        <div className="text-sm text-slate-700 mt-1">
          {display ?? <span className="text-slate-400 italic">None</span>}
        </div>
      )}
    </div>
  );
}

function PosStoresPanel({ merchantId }: { merchantId: string }) {
  const [rows, setRows] = useState<MerchantStoreRow[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const blank = { storeId: "", posDbaName: "", parentStoreId: "", giftCardStoreId: "", note: "" };
  const [form, setForm] = useState(blank);
  const confirm = useConfirm();

  function reload() {
    merchantSubresources.stores.list(merchantId).then(setRows).catch(() => setRows([]));
  }
  useEffect(() => { reload(); }, [merchantId]);

  async function add() {
    if (!form.storeId.trim()) { setErr("Store ID is required."); return; }
    setBusy(true);
    setErr(null);
    try {
      await merchantSubresources.stores.create(merchantId, {
        storeId: form.storeId.trim(),
        posDbaName: form.posDbaName || null,
        parentStoreId: form.parentStoreId || null,
        giftCardStoreId: form.giftCardStoreId || null,
        note: form.note || null,
      });
      setForm(blank);
      setAdding(false);
      reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Couldn't add the store.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!(await confirm({ description: "Remove this store?", confirmLabel: "Remove", destructive: true }))) return;
    try {
      await merchantSubresources.stores.remove(merchantId, id);
      reload();
    } catch {  }
  }

  const fld = (k: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <Card className="overflow-hidden">
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <span className="text-base font-semibold text-slate-900">
          POS Stores {rows ? `(${rows.length})` : ""}
        </span>
        <Button type="button" size="sm" variant="outline" onClick={() => { setAdding((a) => !a); setErr(null); }}>
          {adding ? <X /> : <Plus />}
          {adding ? "Cancel" : "Add store"}
        </Button>
      </div>
      <div className="p-4 text-sm">
        {adding && (
          <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input placeholder="Store ID *" value={form.storeId} onChange={fld("storeId")} maxLength={FIELD_MAX.merchantStore.storeId} className="h-9" />
            <Input placeholder="POS DBA name" value={form.posDbaName} onChange={fld("posDbaName")} maxLength={FIELD_MAX.merchantStore.posDbaName} className="h-9" />
            <Input placeholder="Parent store ID" value={form.parentStoreId} onChange={fld("parentStoreId")} maxLength={FIELD_MAX.merchantStore.parentStoreId} className="h-9" />
            <Input placeholder="Gift-card store ID" value={form.giftCardStoreId} onChange={fld("giftCardStoreId")} maxLength={FIELD_MAX.merchantStore.giftCardStoreId} className="h-9" />
            <Input placeholder="Note" value={form.note} onChange={fld("note")} className="sm:col-span-2 h-9" />
            <div className="sm:col-span-2 flex items-center gap-2">
              <Button type="button" size="sm" onClick={add} disabled={busy}>
                {busy ? "Adding…" : "Add store"}
              </Button>
              {err && <span className="text-xs text-destructive">{err}</span>}
            </div>
          </div>
        )}
        {rows === null ? (
          <Loading>Loading…</Loading>
        ) : rows.length === 0 ? (
          !adding && <p className="text-center text-muted-foreground py-4">No POS stores linked yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs font-medium uppercase tracking-wide">Store ID</TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wide">DBA</TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wide">Parent</TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wide">Gift card</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-mono text-slate-800">{s.storeId}</TableCell>
                  <TableCell className="text-slate-700">{s.posDbaName || <Dash />}</TableCell>
                  <TableCell className="text-slate-600">{s.parentStoreId || <Dash />}</TableCell>
                  <TableCell className="text-slate-600">{s.giftCardStoreId || <Dash />}</TableCell>
                  <TableCell className="text-right">
                    <Button type="button" size="sm" variant="link" className="h-auto p-0 text-destructive" onClick={() => remove(s.id)}>Remove</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </Card>
  );
}

function MerchantTabStrip({
  activeTab, ticketCount, onSelect,
}: {
  activeTab: MerchantTabKey;
  ticketCount?: number;
  onSelect: (key: MerchantTabKey) => void;
}) {
  return (
    <Tabs value={activeTab} onValueChange={(v) => onSelect(v as MerchantTabKey)}>
      <TabsList variant="underline" className="flex-wrap" aria-label="Merchant sections">
        {MERCHANT_TABS.map((t) => {
          const showCount = t.key === "tickets" && typeof ticketCount === "number";
          return (
            <TabsTrigger key={t.key} variant="underline" value={t.key}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d={TAB_ICONS[t.key]} />
              </svg>
              <span>{t.label}</span>
              {showCount && (
                <span className="inline-flex items-center justify-center min-w-[20px] px-1.5 h-5 rounded-full bg-slate-200 text-slate-600 text-[10px] font-semibold tabular-nums">
                  {ticketCount}
                </span>
              )}
            </TabsTrigger>
          );
        })}
      </TabsList>
    </Tabs>
  );
}

function sumStatements(rows: MerchantStatementSummary[], from?: Date, to?: Date) {
  return rows.reduce(
    (acc, s) => {
      const d = new Date(s.periodMonth);
      if (from && d < from) return acc;
      if (to && d >= to) return acc;
      return {
        volume: acc.volume + s.netVolume,
        profit: acc.profit + s.netProfit,
        tickets: acc.tickets + s.totalTickets,
        commission: acc.commission + s.userPayout,
      };
    },
    { volume: 0, profit: 0, tickets: 0, commission: 0 },
  );
}

function ProfileTab({
  m, mid, onMidChange,
}: {
  m: MerchantDetail;
  mid: string;
  onMidChange: (mid: string) => void;
}) {
  const now = new Date();
  const startOfMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const startOfPrevMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  const startOfQuarter = new Date(Date.UTC(now.getUTCFullYear(), Math.floor(now.getUTCMonth() / 3) * 3, 1));
  const startOfYear = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));

  const rows = m.recentStatements;
  const thisMonth = sumStatements(rows, startOfMonth);
  const lastMonth = sumStatements(rows, startOfPrevMonth, startOfMonth);
  const thisQuarter = sumStatements(rows, startOfQuarter);
  const ytd = sumStatements(rows, startOfYear);
  const v12 = rows.reduce((a, s) => a + s.netVolume, 0);
  const p12 = rows.reduce((a, s) => a + s.netProfit, 0);

  const addressLine = m.addressLine1
    ? [m.addressLine1, m.addressLine2, [m.city, m.state, m.zip].filter(Boolean).join(", ")].filter(Boolean).join(", ")
    : <Dash />;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <PeriodTile label="This month" volume={thisMonth.volume} tickets={thisMonth.tickets} commission={thisMonth.commission} accent="emerald" />
        <PeriodTile label="Last month" volume={lastMonth.volume} tickets={lastMonth.tickets} commission={lastMonth.commission} accent="blue" />
        <PeriodTile label="This quarter" volume={thisQuarter.volume} tickets={thisQuarter.tickets} commission={thisQuarter.commission} accent="amber" />
        <PeriodTile label={`YTD ${now.getUTCFullYear()}`} volume={ytd.volume} tickets={ytd.tickets} commission={ytd.commission} accent="brand" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="space-y-5">
          <Panel title="DBA / Agent">
            <div className="flex items-center justify-between gap-3 py-1">
              <div className="min-w-0">
                <span className="font-mono text-xs text-slate-500 mr-2">#{mid}</span>
                <span className="text-sm text-slate-900 font-medium">{m.dbaName}</span>
              </div>
              <StatusPill value={m.status} map={MERCHANT_STATUS_TONES} />
            </div>
            <AgentRow merchantId={m.id} kind="owner" label="Owning agent" initial={m.owner} />
            <AgentRow merchantId={m.id} kind="associate" label="Associate agent" initial={m.associate} />
          </Panel>

          <CollapsiblePanel
            title="Acquiring"
            subtitle={formatDate(m.approvedDate) ? `Approved ${formatDate(m.approvedDate)}` : ""}
            storageKey="acquiring"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              <KpiField label="Legal name" value={m.legalName || <Dash />} />
              <KpiField label="DBA" value={m.dbaName} />
              <KpiField label="MCC" value={m.mcc || <Dash />} />
              <KpiField label="Acquirer" value={m.assocName || <Dash />} />
              <KpiField label="Assoc #" value={m.assocNumber || <Dash />} />
              <KpiField label="Status" value={m.status} />
              <KpiField label="Approved" value={formatDate(m.approvedDate) || "(not set)"} />
              <KpiField label="Closed" value={formatDate(m.closedDate) || "(not set)"} />
              <KpiField label="Last deposit" value={formatDate(m.lastDepositDate) || "(not set)"} />
            </div>
            <div className="pt-3 mt-3 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              <KpiField label="Contact" value={m.contactName || <Dash />} />
              <KpiField label="Email" value={m.email || <Dash />} />
              <KpiField label="Phone" value={m.phone || <Dash />} />
              <KpiField label="Address" value={addressLine} />
            </div>
            <div className="pt-3 mt-3 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              <KpiField label="Tax ID" value={maskSecret(m.taxId ?? "") || <Dash />} />
              <KpiField label="EIN" value={maskSecret(m.ein ?? "") || <Dash />} />
              <KpiField label="SSN" value={maskSecret(m.ssn ?? "") || <Dash />} />
              <KpiField label="Bank account" value={maskSecret(m.bankAccountNumber ?? "") || <Dash />} />
              <KpiField label="Routing" value={maskSecret(m.bankRoutingNumber ?? "") || <Dash />} />
            </div>
          </CollapsiblePanel>

          <CollapsiblePanel title="Sales" subtitle="(boarding application data)" storageKey="sales">
            <div className="grid grid-cols-3 gap-3 text-sm">
              <SalesStat label="In-person" value={<Dash />} />
              <SalesStat label="Mail/Phone" value={<Dash />} />
              <SalesStat label="Internet/eComm" value={<Dash />} />
              <SalesStat label="Avg transaction" value={usdOrDash(m.avgTicketAmount)} />
              <SalesStat label="Max transaction" value={usdOrDash(m.maxTransactionAmount)} />
              <SalesStat label="Monthly volume" value={usdOrDash(m.monthlyVolumeEstimate)} />
            </div>
          </CollapsiblePanel>

          <CollapsiblePanel title="Processing profile" subtitle="credit card processing methods" storageKey="processingProfile" defaultOpen>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2">
              <KpiField label="Card swiped" value={pctStr(m.pctCardSwiped)} />
              <KpiField label="Keyed (card present)" value={pctStr(m.pctKeyedCardPresent)} />
              <KpiField label="Keyed CNP / MOTO" value={pctStr(m.pctKeyedCardNotPresent)} />
              <KpiField label="eCommerce" value={pctStr(m.pctEcommerce)} />
              <KpiField label="B2B" value={pctStr(m.pctB2B)} />
              <KpiField label="Avg ticket" value={usdOrDash(m.avgTicketAmount)} />
              <KpiField label="Monthly volume" value={usdOrDash(m.monthlyVolumeEstimate)} />
            </div>
          </CollapsiblePanel>
        </div>

        <div className="space-y-5">
          <CollapsiblePanel title="Performance" storageKey="performance">
            <div className="grid grid-cols-2 gap-3 text-center text-xs">
              <div>
                <div className="text-slate-500">Gross Sales (T12M)</div>
                <div className="text-slate-900 font-semibold text-sm">{fmtUsd(v12)}</div>
              </div>
              <div>
                <div className="text-slate-500">Net Profit (T12M)</div>
                <div className="text-slate-900 font-semibold text-sm">{fmtUsd(p12)}</div>
              </div>
            </div>
            {rows.length === 0 && (
              <p className="text-center text-xs text-slate-400 mt-3">No statement history yet.</p>
            )}
          </CollapsiblePanel>

          <MidPanel merchant={m} mid={mid} onMidChange={onMidChange} />

          <PosStoresPanel merchantId={m.id} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 items-start">
            <CollapsiblePanel title="Product / service description" storageKey="productServiceDescription">
              <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{m.notes || <Dash />}</p>
            </CollapsiblePanel>
            <CollapsiblePanel title="PCI Compliance" storageKey="pci">
              <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs">
                <div className="font-semibold text-amber-800">NOT Compliant</div>
                <div className="text-amber-700 mt-1">(not yet tracked)</div>
              </div>
            </CollapsiblePanel>
          </div>
        </div>
      </div>

      <CollapsiblePanel title="Risk snapshot" subtitle="(field set under construction)" storageKey="riskSnapshot">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2">
          <KpiField label="Net profit" value={fmtUsd(m.netProfit)} />
          <KpiField label="Month ratio" value={m.thisMonthRatio != null ? m.thisMonthRatio.toFixed(2) : <Dash />} />
          <KpiField label="Prospect #" value={m.prospectNumber || <Dash />} />
          <KpiField label="Branch" value="BLogic Systems" />
        </div>
      </CollapsiblePanel>

      <BoardingDataPanel merchant={m} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MerchantSubresourceSections merchantId={m.id} />
      </div>
    </div>
  );
}

type BoardingFieldDef = { key: string; label: string; type?: "text" | "number"; secret?: boolean };
type BoardingGroup = { title: string; fields: BoardingFieldDef[]; hidden?: boolean };

const BOARDING_GROUPS: BoardingGroup[] = [
  { title: "Business", fields: [
    { key: "legalName", label: "Legal name" },
    { key: "dbaName", label: "DBA name" },
    { key: "mcc", label: "MCC" },
    { key: "ownershipType", label: "Ownership type" },
    { key: "taxIdType", label: "Tax ID type" },
    { key: "taxId", label: "Tax ID", secret: true },
    { key: "assocNumber", label: "TSYS Assoc #" },
    { key: "salesRepCode", label: "Sales rep code" },
    { key: "website", label: "Website" },
    { key: "merchantCustomerServicePhone", label: "Cust. service phone" },
  ]},
  { title: "Legal address", fields: [
    { key: "legalAddress", label: "Address" },
    { key: "legalCity", label: "City" },
    { key: "legalState", label: "State" },
    { key: "legalZip", label: "ZIP" },
  ]},
  { title: "DBA address", fields: [
    { key: "dbaAddress", label: "Address" },
    { key: "dbaCity", label: "City" },
    { key: "dbaState", label: "State" },
    { key: "dbaZip", label: "ZIP" },
  ]},
  { title: "Contact", fields: [
    { key: "contactFirstName", label: "First name" },
    { key: "contactLastName", label: "Last name" },
    { key: "contactTitle", label: "Title" },
    { key: "contactPhone", label: "Phone" },
    { key: "email", label: "Email" },
  ]},
  { title: "Processing", fields: [
    { key: "numberOfLocations", label: "# Locations", type: "number" },
    { key: "avgTransactionAmount", label: "Avg ticket $", type: "number" },
    { key: "monthlySalesTotal", label: "Monthly sales $", type: "number" },
    { key: "cardTypesRequested", label: "Card types" },
    { key: "refundPolicy", label: "Refund policy" },
  ]},
  { title: "Compliance", hidden: true, fields: [
    { key: "siteVerificationOption", label: "Site verification" },
    { key: "patriotActSection", label: "Patriot Act section" },
  ]},
  { title: "eSign signers", hidden: true, fields: [
    { key: "signerGuarantorName", label: "Guarantor name" },
    { key: "signerGuarantorEmail", label: "Guarantor email" },
    { key: "signerPrincipalName", label: "Principal name" },
    { key: "signerPrincipalEmail", label: "Principal email" },
  ]},
];

const VISIBLE_BOARDING_GROUPS = BOARDING_GROUPS.filter((g) => !g.hidden);

function BoardingSecretInput({ value, onChange }: { value: string; onChange: (raw: string) => void }) {
  const [editing, setEditing] = useState(false);
  return (
    <Input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      spellCheck={false}
      value={editing ? value : maskSecret(value)}
      onFocus={(e) => e.currentTarget.select()}
      onBlur={() => setEditing(false)}
      onChange={(e) => { setEditing(true); onChange(e.target.value); }}
      className="h-9"
    />
  );
}

function BoardingDataPanel({ merchant }: { merchant: MerchantDetail }) {
  const initial = useMemo<Record<string, unknown> | null>(() => {
    if (!merchant.boardingDataJson) return null;
    try {
      const parsed = JSON.parse(merchant.boardingDataJson);
      return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }, [merchant.boardingDataJson]);

  const [bag, setBag] = useState<Record<string, unknown>>(initial ?? {});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [appId, setAppId] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    applicationsV2.findByMerchant(merchant.id)
      .then((a) => { if (!cancelled) setAppId(a?.id ?? null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [merchant.id]);

  if (initial === null) return null;

  function set(key: string, type?: "text" | "number") {
    return (raw: string) => {
      setBag((p) => ({
        ...p,
        [key]: type === "number" ? (raw === "" ? null : Number(raw)) : (raw === "" ? null : raw),
      }));
    };
  }

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      await merchants.saveBoarding(merchant.id, bag);
      setSavedAt(new Date());
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Couldn't save boarding data.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <CollapsiblePanel title="Boarding data (TSYS ELAPP)" subtitle="merchant's editable copy" storageKey="boardingData" defaultOpen>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <p className="text-sm text-slate-500 max-w-xl">
          The merchant's own copy of the boarding application fields. Edits here are saved on the
          merchant and don't change the source application.
        </p>
        {appId && (
          <Link
            to={`/applications/v2/${appId}`}
            className="shrink-0 text-sm text-brand-600 hover:underline"
          >
            Open source application →
          </Link>
        )}
      </div>

      {err && <div className="mb-3"><Notice tone="error">{err}</Notice></div>}

      <div className="space-y-5">
        {VISIBLE_BOARDING_GROUPS.map((g) => (
          <div key={g.title}>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 border-b border-slate-100 pb-1">
              {g.title}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {g.fields.map((fld) => {
                const v = bag[fld.key];
                const display = typeof v === "string" ? v : typeof v === "number" ? String(v) : "";
                return (
                  <label key={fld.key} className="block">
                    <span className="block text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">{fld.label}</span>
                    {fld.secret ? (
                      <BoardingSecretInput value={display} onChange={set(fld.key, fld.type)} />
                    ) : (
                      <Input
                        type={fld.type === "number" ? "number" : "text"}
                        value={display}
                        onChange={(e) => set(fld.key, fld.type)(e.target.value)}
                        className="h-9"
                      />
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 mt-5">
        <Button type="button" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save boarding data"}
        </Button>
        {savedAt && <span className="text-xs text-slate-400">Saved {savedAt.toLocaleTimeString()}</span>}
      </div>
    </CollapsiblePanel>
  );
}

function TicketsTab({
  merchantId,
  tickets,
  origin,
}: {
  merchantId: string;
  tickets: TicketListItem[] | null;
  origin: BackOrigin;
}) {
  if (tickets === null) {
    return <Loading>Loading tickets…</Loading>;
  }
  return (
    <Panel
      title={`Tickets (${tickets.length})`}
      wide
    >
      <div className="mb-3">
        <Link
          to={`/tickets/new?merchantId=${merchantId}`}
          state={origin}
          className="text-sm text-brand-600 hover:underline"
        >
          + New ticket for this merchant
        </Link>
      </div>
      {tickets.length === 0 ? (
        <Empty>No tickets for this merchant yet.</Empty>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs font-medium uppercase tracking-wide">#</TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wide">Subject</TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wide">Status</TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wide">Priority</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tickets.map((t) => (
              <TableRow key={t.id}>
                <TableCell className="font-mono text-xs text-slate-600">
                  <Link to={`/tickets/${t.id}`} state={origin} className="text-brand-600 hover:underline">
                    #{t.ticketNumber}
                  </Link>
                </TableCell>
                <TableCell className="text-slate-800">
                  <Link to={`/tickets/${t.id}`} state={origin} className="hover:underline">{t.subject}</Link>
                </TableCell>
                <TableCell><TicketStatusPill status={t.status} /></TableCell>
                <TableCell><PriorityBadge priority={t.priority} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Panel>
  );
}

function ComingSoonTab({ title, body }: { title: string; body: string }) {
  return (
    <Panel title={title} wide>
      <div className="py-8 text-center">
        <p className="text-sm text-slate-500 max-w-lg mx-auto">{body}</p>
      </div>
    </Panel>
  );
}

function StatementsTable({
  rows,
  totalCount,
}: {
  rows: MerchantStatementSummary[];
  totalCount: number;
}) {
  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs font-medium uppercase tracking-wide">Period</TableHead>
            <TableHead className="text-xs font-medium uppercase tracking-wide">Processor</TableHead>
            <TableHead className="text-right text-xs font-medium uppercase tracking-wide">Volume</TableHead>
            <TableHead className="text-right text-xs font-medium uppercase tracking-wide">Tickets</TableHead>
            <TableHead className="text-right text-xs font-medium uppercase tracking-wide">Net Profit</TableHead>
            <TableHead className="text-right text-xs font-medium uppercase tracking-wide">User Payout</TableHead>
            <TableHead className="text-right text-xs font-medium uppercase tracking-wide">Chargebacks</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((s) => (
            <TableRow key={s.id}>
              <TableCell className="text-slate-800">{formatMonth(s.periodMonth)}</TableCell>
              <TableCell className="text-slate-700">{s.processor}</TableCell>
              <TableCell className="text-right tabular-nums">{fmtUsd(s.netVolume)}</TableCell>
              <TableCell className="text-right tabular-nums">{s.totalTickets.toLocaleString()}</TableCell>
              <TableCell className="text-right tabular-nums">{fmtUsd(s.netProfit)}</TableCell>
              <TableCell className="text-right tabular-nums">{fmtUsd(s.userPayout)}</TableCell>
              <TableCell className="text-right tabular-nums">{s.chargebackCount}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {totalCount > rows.length && (
        <p className="text-xs text-slate-500 mt-2">
          Showing the most recent {rows.length} of {totalCount} statements.
        </p>
      )}
    </div>
  );
}

function Panel({ title, wide, children }: { title: string; wide?: boolean; children: ReactNode }) {
  return (
    <Card className={cn("overflow-hidden", wide && "lg:col-span-3")}>
      <div className="px-4 py-3 border-b text-base font-semibold text-slate-900">
        {title}
      </div>
      <div className="p-4 text-sm">{children}</div>
    </Card>
  );
}

function CollapsiblePanel({
  title, subtitle, storageKey, defaultOpen = false, action, children,
}: {
  title: string;
  subtitle?: string;
  storageKey: string;
  defaultOpen?: boolean;
  action?: ReactNode;
  children: ReactNode;
}) {
  const lsKey = `merchant:panel:${storageKey}`;
  const [open, setOpen] = useState<boolean>(() => {
    const saved = localStorage.getItem(lsKey);
    return saved === null ? defaultOpen : saved === "1";
  });
  useEffect(() => {
    localStorage.setItem(lsKey, open ? "1" : "0");
  }, [lsKey, open]);

  return (
    <Card className="overflow-hidden">
      <Accordion
        type="single"
        collapsible
        value={open ? storageKey : ""}
        onValueChange={(v) => setOpen(v === storageKey)}
      >
        <AccordionItem value={storageKey} className="border-b-0">
          <div className="relative">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <span className={cn("flex min-w-0 items-center gap-2", action && "mr-36")}>
                <span className="truncate text-base font-semibold text-slate-900">{title}</span>
                {subtitle && <span className="shrink-0 text-xs font-normal text-slate-400">{subtitle}</span>}
              </span>
            </AccordionTrigger>
            {action && (
              <div className="absolute right-10 top-1/2 -translate-y-1/2">{action}</div>
            )}
          </div>
          <AccordionContent className="border-t border-slate-100 px-4 pb-4 pt-4 text-sm">
            {children}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
}

function PeriodTile({
  label, volume, tickets, commission, accent,
}: {
  label: string;
  volume: number;
  tickets: number;
  commission: number;
  accent: "emerald" | "blue" | "amber" | "brand";
}) {
  const dot =
    accent === "emerald" ? "bg-emerald-500"
      : accent === "blue" ? "bg-blue-500"
      : accent === "amber" ? "bg-amber-500"
      : "bg-primary";
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2">
        <span className={cn("h-2.5 w-2.5 rounded-full", dot)} />
        <span className="text-sm text-muted-foreground">{label}</span>
      </div>
      <div className="text-2xl font-semibold text-slate-900 mt-1 tabular-nums">{fmtUsd(volume)}</div>
      <div className="text-xs text-muted-foreground mt-0.5">
        {tickets.toLocaleString()} tickets · {fmtUsd(commission)} commission
      </div>
    </Card>
  );
}

function KpiField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-slate-900 mt-0.5 break-words">{value}</div>
    </div>
  );
}

function SalesStat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="text-center">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-sm text-slate-900 font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function pctStr(value: number | null): ReactNode {
  return value != null ? `${value}%` : <Dash />;
}

function usdOrDash(value: number | null): ReactNode {
  return value != null ? fmtUsd(value) : <Dash />;
}

function fmtUsd(value: number): string {
  return value.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
}

